// src/services/matrixAuthService.js
// Matrix authentication service - handles login and credential management

import { createClient } from 'matrix-js-sdk';
import logger from '../utils/logger';
import { RECOVERY_SKIPPED_KEY } from '../context/RecoveryContext/storage';

// Module-scoped in-flight promise for recovery detection
let recoveryDetectionInFlight = null;

/**
 * Matrix Authentication Service
 * Handles Matrix login, credential storage, and session management
 */
class MatrixAuthService {
  constructor() {
    this.defaultHomeserver = 'https://matrix-synapse.netsferedev.com';
    this.defaultIdentityServer = 'https://vector.im';
    this.recoveryActions = null; // Will be set by app initialization
  }

  /**
   * Authenticate with Matrix homeserver
   */
  async login(credentials, options = {}) {
    const {
      homeserver = this.defaultHomeserver,
      identityServer = this.defaultIdentityServer,
      loginType = 'm.login.password',
      skipRecoveryCheck = false, // Option to skip recovery detection
    } = options;

    try {
      logger.debug('Attempting Matrix login to:', homeserver);

      // Create temporary client for login
      const tempClient = createClient({
        baseUrl: homeserver,
        idBaseUrl: identityServer,
      });

      // Prepare login parameters
      const loginParams = {
        password: credentials.password,
        identifier: {
          type: 'm.id.user',
          user: credentials.email || credentials.username,
        },
      };

      // Perform login
      const loginResponse = await tempClient.login(loginType, loginParams);
      logger.debug('Matrix login successful:', {
        userId: loginResponse.user_id,
        deviceId: loginResponse.device_id,
      });

      // Create credentials object
      const matrixCredentials = {
        homeserverUrl: homeserver,
        identityServerUrl: identityServer,
        userId: loginResponse.user_id,
        deviceId: loginResponse.device_id,
        accessToken: loginResponse.access_token,
        wellKnown: loginResponse.well_known,
      };

      // If recovery check is not skipped, finalize with recovery detection
      if (!skipRecoveryCheck) {
        const finalizeResult = await this.finalizeLogin(matrixCredentials);
        return {
          success: true,
          credentials: matrixCredentials,
          user: this.extractUserInfo(matrixCredentials),
          client: finalizeResult.client,
        };
      } else {
        // Just store credentials without recovery check
        this.storeCredentials(matrixCredentials);
        return {
          success: true,
          credentials: matrixCredentials,
          user: this.extractUserInfo(matrixCredentials),
        };
      }

    } catch (error) {
      logger.error('Matrix login failed:', error);

      // Return structured error
      return {
        success: false,
        error: {
          code: error.errcode || 'UNKNOWN_ERROR',
          message: error.message || 'Login failed',
          details: error,
        },
      };
    }
  }

  /**
   * Store Matrix credentials securely
   */
  storeCredentials(credentials) {
    try {
      const credentialsJson = JSON.stringify(credentials);
      localStorage.setItem('matrixCreds', credentialsJson);
      logger.debug('Matrix credentials stored');
    } catch (error) {
      logger.error('Failed to store Matrix credentials:', error);
      throw new Error('Failed to save login credentials');
    }
  }

  /**
   * Get stored Matrix credentials
   */
  getStoredCredentials() {
    try {
      const credentialsJson = localStorage.getItem('matrixCreds');
      if (!credentialsJson) {
        return null;
      }

      const credentials = JSON.parse(credentialsJson);
      
      // Validate required fields
      if (!credentials.accessToken || !credentials.userId || !credentials.homeserverUrl) {
        logger.warn('Invalid stored Matrix credentials found');
        this.clearCredentials();
        return null;
      }

      return credentials;
    } catch (error) {
      logger.error('Failed to parse stored Matrix credentials:', error);
      this.clearCredentials();
      return null;
    }
  }

  /**
   * Clear stored credentials
   */
  clearCredentials() {
    try {
      localStorage.removeItem('matrixCreds');
      logger.debug('Matrix credentials cleared');
    } catch (error) {
      logger.error('Failed to clear Matrix credentials:', error);
    }
  }

  /**
   * Check if user has valid stored credentials
   */
  hasValidCredentials() {
    const credentials = this.getStoredCredentials();
    return credentials !== null;
  }

  /**
   * Validate credentials by making a test request
   */
  async validateCredentials(credentials = null) {
    const creds = credentials || this.getStoredCredentials();
    if (!creds) {
      return { valid: false, error: 'No credentials found' };
    }

    try {
      // Create client with credentials
      const testClient = createClient({
        baseUrl: creds.homeserverUrl,
        accessToken: creds.accessToken,
        userId: creds.userId,
        deviceId: creds.deviceId,
      });

      // Test with a simple whoami request
      const whoami = await testClient.whoami();
      
      if (whoami.user_id === creds.userId) {
        return { valid: true };
      } else {
        return { valid: false, error: 'User ID mismatch' };
      }
      
    } catch (error) {
      logger.error('Credential validation failed:', error);
      
      // If credentials are invalid, clear them
      if (error.errcode === 'M_UNKNOWN_TOKEN' || error.errcode === 'M_MISSING_TOKEN') {
        this.clearCredentials();
      }
      
      return { 
        valid: false, 
        error: error.message || 'Credential validation failed',
        code: error.errcode,
      };
    }
  }

  /**
   * Extract user information from credentials
   */
  extractUserInfo(credentials) {
    const userId = credentials.userId;
    
    // Parse Matrix user ID (@username:homeserver.com)
    const userIdMatch = userId.match(/^@([^:]+):(.+)$/);
    const username = userIdMatch ? userIdMatch[1] : userId;
    const homeserver = userIdMatch ? userIdMatch[2] : credentials.homeserverUrl;

    return {
      id: userId,
      username: username,
      displayName: username, // Will be updated from Matrix profile later
      homeserver: homeserver,
      email: null, // Not available from Matrix credentials
      avatar: null, // Will be loaded from Matrix profile later
    };
  }

  /**
   * Logout from Matrix
   */
  async logout() {
    logger.info('LOGOUT: Starting logout process');
    const credentials = this.getStoredCredentials();
    logger.debug('LOGOUT: Current user:', credentials ? `${credentials.userId}:${credentials.deviceId}` : 'none');

    // 1. Stop Matrix client and perform server logout
    if (credentials) {
      try {
        logger.debug('LOGOUT: Importing MatrixService');
        // Import MatrixService to stop client properly
        const { default: matrixService } = await import('./matrixService');

        // Cleanup Matrix service (stops sync, removes listeners, clears client)
        if (matrixService.client) {
          logger.debug('LOGOUT: Cleaning up Matrix service');
          await matrixService.cleanup();
          
          // Wait for crypto resources to be fully released before creating temp client
          // This prevents race conditions with crypto operations during logout
          logger.debug('LOGOUT: Waiting for crypto cleanup to complete...');
          await new Promise(resolve => setTimeout(resolve, 500));
        }

        logger.debug('LOGOUT: Creating temporary client for server logout');
        // Create temporary client for logout
        // Wrap in try-catch to handle potential crypto race conditions gracefully
        try {
          const client = createClient({
            baseUrl: credentials.homeserverUrl,
            accessToken: credentials.accessToken,
            userId: credentials.userId,
            deviceId: credentials.deviceId,
          });

          // Logout from Matrix server
          await client.logout();
          logger.debug('LOGOUT: Matrix server logout successful');
        } catch (logoutError) {
          // Check if this is an expected crypto race condition error
          const errorMsg = logoutError.message || '';
          if (errorMsg.includes('null pointer') || 
              errorMsg.includes('OutgoingRequestsManager') ||
              errorMsg.includes('was stopped')) {
            logger.debug('LOGOUT: Expected crypto race condition during shutdown, continuing with cleanup...');
          } else {
            // Unexpected error - log it but don't throw
            logger.error('LOGOUT: Unexpected server logout error:', logoutError);
          }
          // Continue with local cleanup regardless of server logout result
        }

      } catch (error) {
        logger.error('LOGOUT: Matrix server logout failed:', error);
        // Continue with local cleanup even if server logout fails
      }
    } else {
      logger.debug('LOGOUT: No credentials found, skipping server logout');
    }

    // 2. Clear all localStorage items
    try {
      logger.debug('LOGOUT: Clearing Matrix credentials from localStorage');
      // Clear Matrix credentials
      this.clearCredentials();

      // Clear all Matrix-related localStorage items
      const keysToRemove = [
        'matrixCreds',
        'matrixForceClearStores',
        'matrixForceDeviceReset',
        'matrixResetDeviceKeys',
        'matrixDisableCrypto',
        'matrixKeyConflictDetected',
        'matrixTempPassword',
        'TI-M_FEATURE_FLAGS',
      ];

      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          logger.debug(`Could not remove localStorage key ${key}:`, e);
        }
      });

      logger.debug('LOGOUT: localStorage cleared');
    } catch (error) {
      logger.error('LOGOUT: Failed to clear localStorage:', error);
    }

    // 3. Clear all sessionStorage items (especially recovery state)
    try {
      logger.debug('LOGOUT: Starting sessionStorage cleanup');

      // Clear recovery-related sessionStorage more thoroughly
      const sessionKeysToRemove = [
        'recovery:v1', // Main recovery state key
        'recovery:v0', // Old recovery state key
        'recovery/ek', // Ephemeral key storage
        'recovery_state',
        'recovery_modal_state',
        'matrix_recovery',
      ];

      // Clear all keys that match patterns
      const allSessionKeys = Object.keys(sessionStorage);
      logger.debug(`LOGOUT: Found ${allSessionKeys.length} keys in sessionStorage`);

      allSessionKeys.forEach(key => {
        // Remove any recovery-related keys
        if (key.startsWith('recovery') ||
            key.includes('recovery') ||
            sessionKeysToRemove.includes(key)) {
          try {
            const value = sessionStorage.getItem(key);
            logger.debug(`LOGOUT: Removing sessionStorage key: ${key}, had value: ${value?.substring(0, 50)}...`);
            sessionStorage.removeItem(key);
            logger.debug(`LOGOUT: Successfully removed sessionStorage key: ${key}`);
          } catch (e) {
            logger.debug(`LOGOUT: Could not remove sessionStorage key ${key}:`, e);
          }
        }
      });

      // Verify recovery state is cleared
      const checkRecoveryState = sessionStorage.getItem('recovery:v1');
      if (checkRecoveryState) {
        logger.warn('LOGOUT: recovery:v1 still exists after cleanup!', checkRecoveryState);
      } else {
        logger.debug('LOGOUT: recovery:v1 successfully removed from sessionStorage');
      }

      logger.debug('LOGOUT: sessionStorage cleared');
    } catch (error) {
      logger.error('LOGOUT: Failed to clear sessionStorage:', error);
    }

    // 4. Clear recovery keys and state
    try {
      logger.debug('LOGOUT: Starting recovery state cleanup');

      // Get session ID before clearing credentials
      const sessionId = credentials ? `${credentials.userId}:${credentials.deviceId}` : null;
      logger.debug('LOGOUT: Session ID for recovery cleanup:', sessionId);

      // Import recovery services dynamically to avoid circular deps
      const { default: recoveryKeyValidator } = await import('./RecoveryKeyValidator');
      const { default: recoveryEphemeralStore } = await import('./recoveryEphemeralStore');
      const { clearState: clearRecoveryState } = await import('../context/RecoveryContext/storage');

      // Clear recovery keys for this session
      if (sessionId) {
        logger.debug('LOGOUT: Clearing recovery keys for session');
        recoveryKeyValidator.clear(sessionId);
      }

      // Clear ephemeral store
      logger.debug('LOGOUT: Clearing ephemeral recovery store');
      recoveryEphemeralStore.clear();

      // Clear recovery context state from sessionStorage
      logger.debug('LOGOUT: Calling clearRecoveryState()');
      clearRecoveryState();

      // Double-check recovery state was cleared
      const finalCheck = sessionStorage.getItem('recovery:v1');
      if (finalCheck) {
        logger.error('LOGOUT: recovery:v1 STILL exists after all cleanup attempts!', finalCheck);
        // Force remove it
        sessionStorage.removeItem('recovery:v1');
        logger.debug('LOGOUT: Force removed recovery:v1');
      }

      logger.debug('LOGOUT: Recovery keys and state cleared on logout');
    } catch (error) {
      logger.error('Failed to clear recovery keys on logout:', error);
      // Non-fatal - continue with logout
    }

    // 5. Clear avatar cache (blob URLs and IndexedDB)
    // Note: IndexedDB avatar cache will also be deleted when deleteDatabase() is called,
    // but we clear it here explicitly to revoke blob URLs and prevent memory leaks
    try {
      logger.debug('LOGOUT: Clearing avatar cache and revoking blob URLs');
      
      // Import and call avatar manager cleanup (stops GC timer + clears cache)
      const { avatarManager } = await import('../utils/avatarManager');
      avatarManager.cleanup();
      
      logger.debug('LOGOUT: Avatar cache and blob URLs cleared successfully');
    } catch (error) {
      logger.error('LOGOUT: Failed to clear avatar cache:', error);
      // Non-fatal - continue with logout
    }

    // 6. Clear ALL Matrix-related IndexedDB databases
    // We must delete everything because the Rust crypto SDK uses a shared database
    // that doesn't include user identification, causing conflicts between users
    try {
      logger.debug('LOGOUT: Starting IndexedDB cleanup');

      // Helper function to delete a database with timeout
      const deleteDBWithTimeout = (dbName, timeoutMs = 5000) => {
        return new Promise((resolve) => {
          // Setup timeout
          const timeoutId = setTimeout(() => {
            logger.warn(`LOGOUT: Database deletion timed out for: ${dbName}`);
            resolve(false); // Resolve with failure but don't throw
          }, timeoutMs);

          try {
            const deleteReq = window.indexedDB.deleteDatabase(dbName);

            deleteReq.onsuccess = () => {
              clearTimeout(timeoutId);
              logger.debug(`LOGOUT: Successfully deleted database: ${dbName}`);
              resolve(true);
            };

            deleteReq.onerror = (event) => {
              clearTimeout(timeoutId);
              logger.debug(`LOGOUT: Error deleting database ${dbName}:`, event);
              resolve(false); // Don't throw, just mark as failed
            };

            deleteReq.onblocked = () => {
              clearTimeout(timeoutId);
              logger.warn(`LOGOUT: Database deletion blocked for: ${dbName}`);
              resolve(false); // Database is in use, can't delete
            };
          } catch (e) {
            clearTimeout(timeoutId);
            logger.debug(`LOGOUT: Exception deleting database ${dbName}:`, e);
            resolve(false);
          }
        });
      };

      // Known database names to delete (fallback list)
      const knownDatabases = [
        'matrix-js-sdk::matrix-sdk-crypto', // Rust crypto store
        'matrix-js-sdk:crypto',
        'matrix-js-sdk:riot-web-sync',
        'ti-m-matrix',
        'matrix-store',
      ];

      // Try to get list of databases if API is available
      let databasesToDelete = [];

      if (window.indexedDB.databases && typeof window.indexedDB.databases === 'function') {
        try {
          const databases = await window.indexedDB.databases();

          // Filter for Matrix-related databases
          const matrixDatabases = databases.filter(db => {
            if (!db.name) return false;

            // Delete anything Matrix-related
            return (
              db.name.includes('matrix') ||
              db.name.includes('crypto') ||
              db.name === 'matrix-js-sdk::matrix-sdk-crypto' ||
              db.name.includes('ti-m-matrix') ||
              db.name.includes('matrix-js-sdk') ||
              db.name.includes('matrix-store')
            );
          });

          databasesToDelete = matrixDatabases.map(db => db.name);
          logger.debug(`LOGOUT: Found ${databasesToDelete.length} Matrix databases to delete`);
        } catch (e) {
          logger.debug('LOGOUT: Could not enumerate databases, using fallback list:', e);
          databasesToDelete = knownDatabases;
        }
      } else {
        logger.debug('LOGOUT: databases() API not supported, using fallback list');
        databasesToDelete = knownDatabases;
      }

      // Add known databases to ensure they're included
      const allDatabases = [...new Set([...databasesToDelete, ...knownDatabases])];

      // Delete all databases with timeout
      const deletionPromises = allDatabases.map(dbName =>
        deleteDBWithTimeout(dbName, 3000), // 3 second timeout per database
      );

      // Wait for all deletions but with overall timeout
      const overallTimeout = new Promise(resolve =>
        setTimeout(() => {
          logger.warn('LOGOUT: Overall database cleanup timeout reached');
          resolve();
        }, 10000), // 10 second overall timeout
      );

      await Promise.race([
        Promise.all(deletionPromises),
        overallTimeout,
      ]);

      logger.info('LOGOUT: IndexedDB cleanup completed (or timed out safely)');
    } catch (error) {
      logger.error('LOGOUT: Failed to clear Matrix databases:', error);
      // Continue with logout even if database cleanup fails
    }

    // 7. Clear window state variables and session storage flags
    try {
      window.__recoveryCheckDone = false;
      // Clear the skip flag from sessionStorage to allow recovery modal in next session
      sessionStorage.removeItem(RECOVERY_SKIPPED_KEY);
      logger.debug('LOGOUT: Window state variables and recovery skip flag cleared');
    } catch (error) {
      logger.error('LOGOUT: Failed to clear window state:', error);
    }

    // 8. Final verification that recovery state is cleared
    try {
      const finalRecoveryCheck = sessionStorage.getItem('recovery:v1');
      if (finalRecoveryCheck) {
        logger.error('LOGOUT: WARNING - recovery:v1 still present at end of logout!');
        sessionStorage.removeItem('recovery:v1');
        logger.debug('LOGOUT: Force removed recovery:v1 one final time');
      } else {
        logger.debug('LOGOUT: Verified recovery:v1 is cleared');
      }
    } catch (error) {
      logger.error('LOGOUT: Failed final recovery state check:', error);
    }

    logger.info('LOGOUT: Logout completed successfully');
    return { success: true };
  }

  /**
   * Get available homeservers (for future use)
   */
  getKnownHomeservers() {
    return [
      {
        name: 'Netsfere Dev',
        url: 'https://matrix-synapse.netsferedev.com',
        description: 'Development Matrix server',
      },
      {
        name: 'Matrix.org',
        url: 'https://matrix.org',
        description: 'Official Matrix homeserver',
      },
    ];
  }

  /**
   * Discover homeserver information
   */
  async discoverHomeserver(domain) {
    try {
      const wellKnownUrl = `https://${domain}/.well-known/matrix/client`;
      const response = await fetch(wellKnownUrl);

      if (response.ok) {
        const wellKnown = await response.json();
        return {
          homeserver: wellKnown['m.homeserver']?.base_url,
          identityServer: wellKnown['m.identity_server']?.base_url,
        };
      }

      return null;
    } catch (error) {
      logger.error('Homeserver discovery failed:', error);
      return null;
    }
  }

  /**
   * Set recovery actions dispatcher (to be called from app initialization)
   * @param {Object} actions - Recovery action creators
   */
  setRecoveryActions(actions) {
    this.recoveryActions = actions;
  }

  /**
   * Detect if recovery key is required for the authenticated client
   * @param {MatrixClient} client - Authenticated Matrix client
   * @returns {Promise<void>}
   */
  async detectRecoveryRequirement(client) {
    logger.debug('RECOVERY: [AuthService] detectRecoveryRequirement called');
    // Coalesce concurrent calls with module-scoped promise
    if (recoveryDetectionInFlight) {
      logger.debug('RECOVERY: [AuthService] Recovery detection already in progress, waiting...');
      return recoveryDetectionInFlight;
    }

    recoveryDetectionInFlight = this._performRecoveryDetection(client);

    try {
      await recoveryDetectionInFlight;
    } finally {
      recoveryDetectionInFlight = null;
    }
  }

  /**
   * Internal method to perform recovery detection with timeout
   * @private
   */
  async _performRecoveryDetection(client) {
    logger.debug('RECOVERY: [AuthService] Starting recovery detection');
    const timeout = 10000; // 10 seconds timeout
    const timeoutPromise = new Promise((resolve) => {
      setTimeout(() => {
        logger.warn('RECOVERY: [AuthService] Recovery detection timeout - using conservative defaults');
        resolve({
          hasBackup: false,
          backupInfo: null,
          hasSSSS: false,
          needsRecovery: false,
        });
      }, timeout);
    });

    const detectionPromise = this._detectRecoveryInternal(client);

    const result = await Promise.race([detectionPromise, timeoutPromise]);

    // Dispatch only the public fields to the context (no internals)
    const dispatchData = {
      hasBackup: !!result.hasBackup,
      backupInfo: result.backupInfo || null,
      hasSSSS: !!result.hasSSSS,
      needsRecovery: !!result.needsRecovery,
    };

    if (this.recoveryActions) {
      logger.debug('RECOVERY: [AuthService] Dispatching init with:', dispatchData);
      this.recoveryActions.init(dispatchData);
    } else {
      logger.warn('RECOVERY: [AuthService] Recovery actions not configured - state update skipped');
    }
  }

  /**
   * Internal implementation of recovery detection logic
   * @private
   */
  async _detectRecoveryInternal(client) {
    const result = {
      hasBackup: false,
      backupInfo: null,
      hasSSSS: false,
      needsRecovery: false,
    };

    try {
      // Ensure crypto is initialized
      if (!client.getCrypto()) {
        logger.debug('Crypto not initialized, attempting to initialize...');

        // Only initialize crypto, don't start the client sync here
        // The sync should be started by MatrixClientProvider to avoid conflicts
        try {
          await client.initCrypto();
          logger.debug('Crypto initialized for recovery status check');

          // Wait a moment for crypto to be ready
          await new Promise(resolve => setTimeout(resolve, 500));
        } catch (cryptoError) {
          logger.warn('Could not initialize crypto for recovery check:', cryptoError.message);
          // Continue anyway - recovery status can still be partially determined
        }
      }

      // Run parallel checks where safe
      const [backupStatus, ssssStatus, cryptoStatus] = await Promise.all([
        this._checkBackupStatus(client),
        this._checkSSSSStatus(client),
        this._checkCryptoStatus(client),
      ]);

      result.hasBackup = backupStatus.hasBackup;
      result.backupInfo = backupStatus.backupInfo;
      result.hasSSSS = ssssStatus.hasSSSS;

      // Determine if recovery is needed
      const { xsignStatus, deviceTrust } = cryptoStatus;
      const backupTrusted = !!backupStatus.trusted;
      const hasUntrustedBackup = result.hasBackup && !backupTrusted;

      result.needsRecovery = !!result.hasBackup &&
                             (hasUntrustedBackup || !xsignStatus?.ready || deviceTrust?.isVerified !== true);

      logger.debug('RECOVERY: [AuthService] Detection complete:', {
        hasBackup: result.hasBackup,
        hasSSSS: result.hasSSSS,
        needsRecovery: result.needsRecovery,
        backupTrusted,
        hasUntrustedBackup,
        xsignReady: xsignStatus?.ready,
        deviceVerified: deviceTrust?.isVerified,
      });

    } catch (error) {
      logger.error('Recovery detection error - using conservative defaults:', error);
      // Return conservative defaults on error with error info
      result.error = error.message || 'Recovery detection failed';
    }

    return result;
  }

  /**
   * Check backup status
   * @private
   */
  async _checkBackupStatus(client) {
    try {
      // Prefer Rust crypto APIs when available as they are more reliable
      const crypto = typeof client.getCrypto === 'function' ? client.getCrypto() : null;

      if (crypto && typeof crypto.getKeyBackupInfo === 'function') {
        // getKeyBackupInfo returns null if no backup exists
        const info = await crypto.getKeyBackupInfo();
        if (!info) {
          return { hasBackup: false, backupInfo: null };
        }

        // Determine trust via crypto.isKeyBackupTrusted when available
        let isTrusted = false;
        if (typeof crypto.isKeyBackupTrusted === 'function') {
          try {
            const trust = await crypto.isKeyBackupTrusted(info);
            isTrusted = !!(trust && trust.trusted === true);
          } catch {
            // If trust check fails, treat as untrusted (do not throw)
            isTrusted = false;
          }
        }

        // Sanitize to safe fields only
        const sanitizedInfo = {
          algorithm: info.algorithm || 'unknown',
          version: info.version || 'unknown',
          etag: info.etag || null,
        };

        return { hasBackup: true, backupInfo: sanitizedInfo, trusted: isTrusted };
      }

      // Fallback to client-level APIs
      const [isBackupEnabled, backupInfo] = await Promise.all([
        typeof client.isKeyBackupEnabled === 'function' ? client.isKeyBackupEnabled() : Promise.resolve(false),
        typeof client.getKeyBackupVersion === 'function' ? client.getKeyBackupVersion() : Promise.resolve(null),
      ]);

      const hasBackupOnServer = !!(backupInfo && backupInfo.version);
      if (!hasBackupOnServer) {
        return { hasBackup: false, backupInfo: null };
      }

      // Sanitize backup info - only include safe fields
      const sanitizedInfo = {
        algorithm: backupInfo.algorithm || 'unknown',
        version: backupInfo.version || 'unknown',
        etag: backupInfo.etag || null,
      };

      // In the legacy path, treat "enabled" as proxy for trusted
      return { hasBackup: true, backupInfo: sanitizedInfo, trusted: !!isBackupEnabled };
    } catch (error) {
      logger.error('Error checking backup status:', error);
      return { hasBackup: false, backupInfo: null };
    }
  }

  /**
   * Check SSSS (Secure Secret Storage and Sharing) status
   * @private
   */
  async _checkSSSSStatus(client) {
    try {
      const ssssEvent = await client.getAccountData('m.secret_storage.default_key');
      return { hasSSSS: !!ssssEvent };
    } catch (error) {
      logger.error('Error checking SSSS status:', error);
      return { hasSSSS: false };
    }
  }

  /**
   * Check crypto status (cross-signing and device trust)
   * @private
   */
  async _checkCryptoStatus(client) {
    const crypto = client.getCrypto();

    if (!crypto) {
      logger.warn('Crypto not available');
      return { xsignStatus: null, deviceTrust: null };
    }

    try {
      const [xsignStatus, deviceTrust] = await Promise.all([
        crypto.getCrossSigningStatus().catch(err => {
          logger.error('Error getting cross-signing status:', err);
          return null;
        }),
        crypto.getDeviceVerificationStatus(
          client.getUserId(),
          client.getDeviceId(),
        ).catch(err => {
          logger.error('Error getting device verification status:', err);
          return null;
        }),
      ]);

      return { xsignStatus, deviceTrust };
    } catch (error) {
      logger.error('Error checking crypto status:', error);
      return { xsignStatus: null, deviceTrust: null };
    }
  }

  /**
   * Finalize login process with recovery detection
   * @param {Object} matrixCredentials - Successful login credentials
   */
  async finalizeLogin(matrixCredentials) {
    try {
      // Store credentials first
      this.storeCredentials(matrixCredentials);

      // Mark recovery check as done for this session
      window.__recoveryCheckDone = true;

      // Attempt to rehydrate recovery key for session
      try {
        const sessionId = `${matrixCredentials.userId}:${matrixCredentials.deviceId}`;
        const { default: recoveryKeyValidator } = await import('./RecoveryKeyValidator');

        const rehydrated = await recoveryKeyValidator.rehydrate(sessionId);
        if (rehydrated) {
          logger.debug('Recovery key rehydrated from storage');
        }
      } catch (error) {
        logger.debug('Recovery key rehydration skipped:', error.message);
        // Non-fatal - continue without rehydration
      }

      // Return success immediately - recovery detection will be done by the main app
      return {
        success: true,
        client: null, // Client will be created by MatrixService
      };

    } catch (error) {
      logger.error('Error finalizing login:', error);
      // Don't throw - just log and return success since credentials are stored
      return {
        success: true,
        client: null,
      };
    }
  }

  /**
   * Handle SSO callback with recovery detection
   * @param {string} accessToken - SSO access token
   * @param {string} deviceId - Device ID from SSO
   * @param {Object} options - Additional SSO options
   */
  async handleSSOCallback(accessToken, deviceId, options = {}) {
    try {
      // Prevent duplicate detection
      if (window.__recoveryCheckDone) {
        logger.debug('Recovery check already performed this session');
        return { success: true };
      }

      const matrixCredentials = {
        homeserverUrl: options.homeserver || this.defaultHomeserver,
        identityServerUrl: options.identityServer || this.defaultIdentityServer,
        userId: options.userId,
        deviceId: deviceId,
        accessToken: accessToken,
      };

      // Finalize login with recovery detection
      return await this.finalizeLogin(matrixCredentials);

    } catch (error) {
      logger.error('SSO callback handling failed:', error);
      throw error;
    }
  }
}

// Create singleton instance
const matrixAuthService = new MatrixAuthService();

export default matrixAuthService;
